/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : HomologationStatusGraph.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import { PieChart, Pie } from "recharts";
import { Box, Typography } from "@mui/material";

const COLORS = ["#007CB0", "#26890D"];

const renderCustomPieLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  const radius = outerRadius + 20; // distance for label
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
    >
      {(percent * 100).toFixed(1)}%
    </text>
  );
};

renderCustomPieLabel.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  midAngle: PropTypes.number,
  outerRadius: PropTypes.number,
  percent: PropTypes.number,
};

function HomologationStatusGraph({ data = [] }) {
  const chartData = (data || []).slice(1).map((row, index) => ({
    name: row[0],
    value: row[1],
    fill: COLORS[index % COLORS.length],
  }));

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        height: 350,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "visible",
        }}
      >
        <PieChart
          width={300}
          height={260}
          accessibilityLayer={false}
          style={{
            overflow: "visible",
          }}
        >
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={0}
            stroke="transparent"
            style={{ pointerEvents: "none" }}
            labelLine={false}
            isAnimationActive={false}
            label={renderCustomPieLabel}
          />
        </PieChart>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          mt: 2,
        }}
      >
        {chartData.map((item, idx) => (
          <Box
            key={`legend-${item.name || idx}`}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: COLORS[idx % COLORS.length],
              }}
            />
            <Typography variant="body2" sx={{ fontSize: "7px" }}>
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

HomologationStatusGraph.propTypes = {
  data: PropTypes.array,
};

export default HomologationStatusGraph;
